import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Div, Icon, Input, Text } from '@pinpoint/design-kit';
import get from 'lodash.get';

import { ModelsDropdown } from 'src/components';
import { DialogActionProp, ModelProp } from 'src/store/typings';
import BaseDialog from './base';

const defaultValues = {
  model: '',
  description: '',
};

function CreateRealtimeConfig({ isOpen, actions, models, profile }) {
  const [model, setModel] = useState(null);
  const { handleSubmit, errors, control, setValue, reset, register } = useForm({ defaultValues });
  const onSubmit = useCallback(
    (data) => {
      const modelId = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'id'
      );
      const modelName = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'name'
      );
      const modelFramework = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'framework'
      );
      const dataModelType = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'dataModelType'
      );
      const dataModelSubType = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'dataModelSubType'
      );
      const TrainingType = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'trainingType'
      );
      const modelRefreshId = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'modelData.metadataPayload.refresh.name',
        'RF39'
      );
      const refresh = modelRefreshId.match(/\d+/) ? modelRefreshId.match(/\d+/)[0] : '39';
      const modelPatform = get(
        models.find((m) => get(m, 'id') === data.model.id),
        'modelData.metadataPayload.refresh.platform',
        1
      );

      const newConfig = {
        score_type: 'model',
        config_metadata: {
          model_id: modelId,
          modelName,
          dataModelType,
          dataModelSubType,
          v: refresh,
          profile,
          TrainingType,
          platform: modelPatform,
          framework: modelFramework,
        },
        description: data.description.trim(),
        default: false,
        enabled: false,
      };
      actions.handlers[0].on(newConfig);
      actions.handlers[0].after();
      actions.close.on();
    },
    [actions.close, actions.handlers, models, profile]
  );
  const formSubmit = handleSubmit(onSubmit);

  const resetForm = useCallback(() => {
    reset(defaultValues);
    setModel(null);
  }, [reset]);

  useEffect(() => {
    register({ name: 'model', type: 'custom' }, { required: 'Model is required' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelection = useCallback(
    (nextModel) => {
      setValue('model', nextModel);
      setModel(nextModel);
    },
    [setValue]
  );

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      hideCloseIcon
      actions={{
        ...actions,
        cancel: {
          ...actions.cancel,
          before: resetForm,
        },
        close: {
          ...actions.close,
          before: resetForm,
        },
        handlers: [
          {
            on: formSubmit,
          },
        ],
      }}
      items={{
        buttons: [
          {
            text: 'CREATE',
            textSize: 'body',
            close: false,
          },
        ],
        content: [
          <Div key="form-1" d="flex" flexDir="column" p={{ r: '.25rem' }}>
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Select a model
            </Text>
            <ModelsDropdown
              dropDownStyle={{
                'data-test': 'model-dropdown',
                w: '100%',
                h: '1.875rem',
                m: { y: '.375rem' },
                DropdownButtonOptions: {
                  'data-test': 'model-dropdown-btn',
                  h: '1.875rem',
                  w: '1.875rem',
                  rounded: { tr: 'md', br: 'md' },
                  border: { y: '1px solid', r: '1px solid', l: '0' },
                  borderColor: 'shade79',
                  align: 'center',
                  justify: 'center',
                  d: 'flex',
                  p: '0',
                },
                inputStyle: {
                  'data-test': 'model-dropdown-input',
                  h: 'calc(1.875rem-1px)',
                  textColor: 'shade99',
                  flexGrow: '1',
                  p: { l: '0' },
                  rounded: 'none',
                },
                menuOptions: {
                  pos: 'absolute',
                  shadow: 4,
                  minW: '100%',
                  rounded: 'md',
                  maxH: '250px',
                  bg: 'white',
                  overflow: 'auto',
                },
                menuItemOptions: {
                  d: 'flex',
                  align: 'center',
                  p: { x: '1rem', y: '0.5rem' },
                  hoverBg: 'gray100',
                  bg: 'white',
                  'data-test': 'model-dropdown-menu-item',
                },
                DivOptions: {
                  rounded: { l: 'md', r: '0' },
                  border: '1px solid',
                  borderColor: 'shade79',
                  p: { l: '0.75rem' },
                },
                openSuffix: <Icon color="shade66" name="UpArrow" size=".625rem" />,
                closeSuffix: <Icon color="shade66" name="DownArrow" size=".625rem" />,
              }}
              models={models}
              model={model}
              defaultText="Please select a model"
              onSelectModel={handleSelection}
            />
            <ErrorMessage
              errors={errors}
              name="model"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,
          <Div key="form-2" d="flex" flexDir="column" p={{ r: '.25rem' }}>
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Enter a description of the model (optional)
            </Text>
            <Controller
              borderColor="shade79"
              name="description"
              as={Input}
              control={control}
              m={{ t: '.25rem' }}
              h="1.875rem"
            />
          </Div>,
        ],
      }}
      title={{
        text: 'Select a Model',
        backgroundColor: 'marigold',
        color: 'white',
        textSize: 'title',
        icon: (
          <Icon
            name="AddBoxed"
            viewBox="0 0 14 14"
            size="2.375rem"
            color="white"
            alignSelf="center"
            justifySelf="center"
          />
        ),
      }}
    />
  );
}

CreateRealtimeConfig.propTypes = {
  profile: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  models: PropTypes.arrayOf(ModelProp).isRequired,
  actions: DialogActionProp.isRequired,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
};

export default CreateRealtimeConfig;
